/**
 * MascotDialog
 *
 * Shared shell for every mascot moment in the Draft Board Strategy
 * Establishment flow: the opener, the confirm-details step, and the
 * locking-in takeover. Two layouts:
 *   - Default: a centred modal over a dimmed page (ModalPortal). Content
 *     scrolls independently of the action buttons, which stay pinned in a
 *     ModalFooter — otherwise a tall field list (edit mode, a long draft's
 *     pick-preview chips) pushes the buttons below the fold with no way to
 *     reach them.
 *   - fullPage: no backdrop/card chrome, just the mascot centred over the
 *     page itself — used for the "Locking in your strategy" takeover,
 *     which isn't a dialog you can dismiss.
 *
 * Props:
 *   state     — JAIMascot state ('idle' | 'thinking' | 'happy' | 'focused' | ...)
 *   size      — mascot pixel size
 *   headline  — bold line under the mascot
 *   body      — supporting text/node under the headline
 *   actions   — [{ label, onClick, primary?, loading?, disabled? }]
 *   fullPage  — render without modal chrome (default false)
 *   onClose   — backdrop/Escape close handler (modal mode only)
 *   closable  — whether the modal can be dismissed (modal mode only)
 *   width     — modal max-width (modal mode only)
 *   children  — extra content below body, above actions (e.g. the confirm-
 *               details field list)
 */

import { JAIMascot } from '../JAIMascot'
import ModalPortal, { ModalFooter, SolidButton, GhostButton } from '../ModalPortal'
import { JAI_COLORS, JAI_FONTS } from '../JaiBrand'
import { useContentOffset } from '../../context/SidebarContext'

function ActionButton({ action }) {
  return action.primary ? (
    <SolidButton onClick={action.onClick} loading={action.loading} disabled={action.disabled}>{action.label}</SolidButton>
  ) : (
    <GhostButton onClick={action.onClick} disabled={action.disabled}>{action.label}</GhostButton>
  )
}

/** Centred, wrapping row — used only by the fullPage takeover, which has no
 *  footer bar of its own to pin buttons to. */
function CenteredButtons({ actions }) {
  if (!actions.length) return null
  return (
    <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 24, flexWrap: 'wrap' }}>
      {actions.map((a, i) => <ActionButton key={i} action={a} />)}
    </div>
  )
}

export default function MascotDialog({
  state = 'idle', size = 200, headline, body, actions = [], fullPage = false,
  onClose, closable = true, width = 440, children,
}) {
  const contentOffset = useContentOffset()

  const mascotBlock = (
    <div style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 20 }}>
        <JAIMascot size={size} state={state} animate />
      </div>
      {headline && (
        <div style={{ fontSize: 19, fontWeight: 800, color: JAI_COLORS.navy, marginBottom: body ? 8 : 0, fontFamily: JAI_FONTS.tight }}>
          {headline}
        </div>
      )}
      {body && <div style={{ fontSize: 14, color: JAI_COLORS.mute, lineHeight: 1.55, fontFamily: JAI_FONTS.tight }}>{body}</div>}
      {children}
    </div>
  )

  if (fullPage) {
    return (
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, left: contentOffset, zIndex: 100, background: JAI_COLORS.paper,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        transition: 'left 220ms cubic-bezier(0.4,0,0.2,1)',
      }}>
        <div>
          {mascotBlock}
          <CenteredButtons actions={actions} />
        </div>
      </div>
    )
  }

  // Non-primary buttons on the left, primary on the right — matches every
  // other ModalFooter in the app (see AccountMenu's log-out confirm,
  // HelpModal's Cancel/Send), regardless of the order callers pass `actions`
  // in (existing callers pass primary first, to read naturally top-to-bottom
  // when they were still rendered as a centred stack).
  const orderedActions = [...actions].sort((a, b) => (a.primary ? 1 : 0) - (b.primary ? 1 : 0))

  return (
    <ModalPortal onClose={onClose} closable={closable} width={width}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '32px 32px 24px' }}>
        {mascotBlock}
      </div>
      {orderedActions.length > 0 && (
        <ModalFooter>
          {orderedActions.map((a, i) => <ActionButton key={i} action={a} />)}
        </ModalFooter>
      )}
    </ModalPortal>
  )
}
